import React, { useRef, useState, useEffect } from "react";
import { Button, Grid, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import styles from "../styles/autorizationPayments.module.scss";
import { Field, Form, Formik } from "formik";
import {
  generateUploadLinkRestartPaymentPackage,
  putUploadDocBlob,
  restartPaymentPackage,
  executeRestartPaymentPackage,
} from "shared/api/services";
import Swal from "sweetalert2";

const MIN_FIELD_LENGTH = 150;

export type RevertExportFormValues = {
  reason: string;
  justification: string;
  attachment: File | null;
  attachmentId?: number;
  confirmationCode?: string;
};

type FormRevertExportProps = {
  onClose: () => void;
  onSuccess: () => void;
  consecutive: string;
};

const initialValues: RevertExportFormValues = {
  reason: "",
  justification: "",
  attachment: null,
  confirmationCode: "",
};

export const FormRevertExport: React.FC<FormRevertExportProps> = ({
  onClose,
  onSuccess,
  consecutive,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes in seconds

  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    setIsUploading(true);
    setFieldValue("attachment", file);

    try {
      const uploadLinks = await generateUploadLinkRestartPaymentPackage({
        fileName: [file.name],
      });

      if (uploadLinks && uploadLinks.length > 0) {
        const uploadData = uploadLinks[0];
        await putUploadDocBlob(uploadData.link, uploadData.id, uploadData.originalName, file);
        setFieldValue("attachmentId", uploadData.id);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setFieldValue("attachment", null);
      setFieldValue("attachmentId", undefined);
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  // Timer effect for confirmation code expiration
  useEffect(() => {
    if (showConfirmation) {
      setTimeLeft(30 * 60); // Reset to 30 minutes when confirmation is shown
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
            // Show expiration message and reset form
            Swal.fire({
              title: "Código expirado",
              text: "El código de confirmación ha expirado. Por favor, solicite una nueva autorización.",
              icon: "warning",
              confirmButtonText: "Aceptar",
            }).then(() => {
              setShowConfirmation(false);
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [showConfirmation]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={styles.container}>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => {
          if (!showConfirmation) {
            setIsSubmitting(true);
            try {
              await restartPaymentPackage({
                consecutive,
                motive: values.reason,
                detail: values.justification,
                attachmentId: values.attachmentId,
              });
              setShowConfirmation(true);
            } catch (error) {
              console.error("Error al generar código de confirmación:", error);
            } finally {
              setIsSubmitting(false);
            }
          } else {
            setIsSubmitting(true);
            try {
              const result = await executeRestartPaymentPackage({
                consecutive,
                code: values.confirmationCode || "",
              });
              Swal.fire({
                title: "Reversión exitosa",
                text: `El paquete de exporte ${result} fue reversado y las facturas se encuentran nuevamente disponibles para ser incluidas en un nuevo proceso de pago.`,
                icon: "success",
                confirmButtonText: "Aceptar",
              });
              onSuccess();
              onClose();
            } catch (error) {
              console.error("Error al ejecutar reversión:", error);
            } finally {
              setIsSubmitting(false);
            }
          }
        }}
        enableReinitialize={true}
        validateOnBlur={true}
        validateOnChange={false}
        validate={(formValues) => {
          const currentErrors: Partial<Record<keyof RevertExportFormValues, string>> = {};

          if (!formValues.reason?.trim()) {
            currentErrors.reason = "El motivo es obligatorio";
          } else if (formValues.reason.trim().length < MIN_FIELD_LENGTH) {
            currentErrors.reason = `El motivo debe tener mínimo ${MIN_FIELD_LENGTH} caracteres`;
          }
          if (!formValues.justification?.trim()) {
            currentErrors.justification = "La justificación es obligatoria";
          } else if (formValues.justification.trim().length < MIN_FIELD_LENGTH) {
            currentErrors.justification = `La justificación debe tener mínimo ${MIN_FIELD_LENGTH} caracteres`;
          }
          if (showConfirmation && (!formValues.confirmationCode?.trim() || formValues.confirmationCode.length < 6)) {
            currentErrors.confirmationCode = "El código debe tener mínimo 6 caracteres";
          }

          return currentErrors;
        }}
      >
        {(formik) => (
          <Form
            className={styles.modalRevertForm}
            data-testid="form-revert-export"
          >
            <Grid container>
              <Grid item xs={12}>
                <div className={styles.modalRevertItem}>
                  <label htmlFor="reason" className={`${styles.modalRevertLabel} text-required`}>
                    Motivo de la reversión
                  </label>
                  <Field
                    id="reason"
                    name="reason"
                    type="text"
                    placeholder="Ingresa el motivo de la reversión"
                    className={`${styles.modalRevertInput} ${
                      formik.touched.reason && formik.errors.reason ? styles.modalRevertInputError : ""
                    }`}
                    data-testid="input-revert-reason"
                  />
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.touched.reason && formik.errors.reason ? formik.errors.reason : "\u00A0"}
                    </span>
                    <span className={styles.modalRevertCounter}>
                      {`${formik.values.reason?.length ?? 0} caracteres (mínimo ${MIN_FIELD_LENGTH})`}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={styles.modalRevertItem}>
                  <label htmlFor="justification" className={`${styles.modalRevertLabel} text-required`}>
                    Justificación detallada de la reversión
                  </label>
                  <Field
                    as="textarea"
                    id="justification"
                    name="justification"
                    placeholder="Describe los motivos y observaciones de la reversión"
                    rows={4}
                    className={`${styles.modalRevertTextarea} ${
                      formik.touched.justification && formik.errors.justification
                        ? styles.modalRevertInputError
                        : ""
                    }`}
                    data-testid="input-revert-justification"
                  />
                  <div className={styles.modalRevertMeta}>
                    <span className={styles.modalRevertError}>
                      {formik.touched.justification && formik.errors.justification
                        ? formik.errors.justification
                        : "\u00A0"}
                    </span>
                    <span className={styles.modalRevertCounter}>
                      {`${formik.values.justification?.length ?? 0} caracteres (mínimo ${MIN_FIELD_LENGTH})`}
                    </span>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12}>
                <div className={styles.modalRevertItem}>
                  <label className={styles.modalRevertLabel}>
                    Adjuntos de soporte (opcional)
                  </label>
                  <div className={`${styles.modalRevertUpload} ${styles.modalRevertUploadCompact}`}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      onChange={(e) => handleFileSelection(e, formik.setFieldValue)}
                      data-testid="input-attachment"
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      onClick={triggerFilePicker}
                      disabled={isUploading}
                      className={`${styles.modalRevertUploadButton} ${styles.modalRevertUploadButtonCompact}`}
                      data-testid="btn-upload-attachment"
                    >
                      {isUploading ? "Subiendo..." : formik.values.attachment ? "Cambiar archivo" : "Seleccionar archivo"}
                    </Button>
                    {formik.values.attachment && (
                      <div className={styles.modalRevertFileInfo}>
                        <Typography component="span" className={styles.modalRevertFileName}>
                          {formik.values.attachment.name}
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            formik.setFieldValue("attachment", null);
                            formik.setFieldValue("attachmentId", undefined);
                          }}
                          className={styles.modalRevertRemoveFile}
                          data-testid="btn-remove-attachment"
                        >
                          Quitar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Grid>

              {showConfirmation && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <div className={styles.modalRevertItem}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label className={`${styles.modalRevertLabel} text-required`}>
                        Código confirmación
                      </label>
                      <Typography
                        variant="caption"
                        sx={{
                          color: timeLeft === 0 || timeLeft <= 60
                            ? '#d32f2f'
                            : 'rgba(var(--general-color-r), var(--general-color-g), var(--general-color-b), 1)',
                          fontWeight: 500,
                          fontSize: '0.8rem'
                        }}
                      >
                        {timeLeft > 0 ? `Código expira: ${formatTime(timeLeft)}` : 'Código expirado'}
                      </Typography>
                    </div>
                    <Field
                      name="confirmationCode"
                      type="text"
                      placeholder="Ingrese el código de confirmación"
                      className={styles.modalRevertInput}
                      data-testid="input-confirmation-code"
                      disabled={timeLeft === 0}
                    />
                    <div className={styles.modalRevertMeta}>
                      <span className={styles.modalRevertError}>
                        {formik.touched.confirmationCode && formik.errors.confirmationCode
                          ? formik.errors.confirmationCode
                          : "\u00A0"}
                      </span>
                    </div>
                  </div>
                </Grid>
              )}

              <Grid item xs={12}>
                <div className={styles.containerButtons}>
                  <Button
                    className={styles.buttonCancel}
                    onClick={() => {
                      onClose();
                      formik.resetForm();
                    }}
                    data-testid="button-cancel"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    data-testid="button-submit"
                    variant="contained"
                    disabled={
                      isSubmitting ||
                      (showConfirmation && timeLeft === 0) ||
                      !showConfirmation
                        ? !formik.isValid || !formik.values.reason?.trim() || !formik.values.justification?.trim()
                        : !formik.isValid || !formik.values.confirmationCode?.trim() || formik.values.confirmationCode.length < 6
                    }
                    className={`${styles.authorizationBtnFilters} ${styles.modalRevertSubmit}`}
                  >
                    {isSubmitting ? "Procesando..." : showConfirmation ? "Ejecutar reversión" : "Solicitar autorización"}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};
